import { computed, makeObservable, observable, runInAction } from "mobx";
import client from "../feathers";

import { Data } from "./data";
import { ILog, DataType, LogEventType, LogDataType, Services, JSONValue, IItem, ItemType } from "../types";
import { Deliverable, Item, Question } from ".";
import { rootStore } from "src/app";
import { format } from "date-fns";
import i18next from "src/i18n";

// TODO: Extending Data gives us some properties we don't want (mainly created_by, updated_by and updated_at).
//       We can't really avoid it since that's how inheritance works in javascript. We should probably extend
//       a different base class (or none at all).
export class Log extends Data<ILog> implements ILog {
  property?:   string       = null;
  data?:       JSONValue    = null;  // Arbitrary JSON, depends on the column and change type.
  data_type:   LogDataType  = null;
  user_id:     number       = null;
  item_id?:    number       = null;
  room_id?:    number       = null;
  meeting_id?: number       = null;
  space_id?:   number       = null;
  event_type:  LogEventType = null;
  item?:       Item<IItem>  = null;

  logText: any = null;

  protected constructor(data: ILog) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Log, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      logText:    observable,
      getLogRow:  computed,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    this.fromData(data);
    this.service = client.service(Services.Log) as any;
  }

  public static create(data?: ILog): Log {
    return new Log({
      ...data,
      ...Data.create(data),
    });
  }

  protected fromData(data: ILog): void {
    super.fromData(data);
    this.property     = data.property;
    this.data         = data.data;
    this.data_type    = data.data_type;
    this.item_id      = data.item_id && Number(data.item_id);
    this.room_id      = data.room_id && Number(data.room_id);
    this.space_id     = data.space_id && Number(data.space_id);
    this.meeting_id   = data.meeting_id && Number(data.meeting_id);
    this.user_id      = data.user_id && Number(data.user_id);
    this.event_type   = data.event_type;
    this.item         = data.item && Item.createType(data.data_type as ItemType, data.item);
  }

  public toData(): any {
    throw new Error('Cannot create logs on the client.')
  }

  // private parseLogText = async () => {
  //   const t = await rootStore.logStore.logParser.parse(this, true, i18next.language);
  //   runInAction(() => this.logText = t);
  // }

  get getLogRow(): {[key: string]: string | number | Log} {
    switch(this.data_type) {
      case DataType.Question:
        return {
          item_id:       this.item_id,
          room_id:       this.room_id,
          number:        this.item.number,
          from:          rootStore.userStore.getItemByID((this.item as Question)["from"])?.getFullName || null,
          to:            rootStore.userStore.getItemByID((this.item as Question)["to"])?.getFullName || null,
          title:         this.item.title,
          created_at:    format(this.created_at, "yyyy-MM-dd HH:mm"),
          response_date: this.item.date ? format(this.item.date, "yyyy-MM-dd") :  "",
          status:        i18next.t(this.item.getStatus) as string,
          _status:       this.item.getStatus,
          meetingroom:   rootStore.spaceStore.getItemByID(this.item.room_id)?.title || null,
        }
      case DataType.Deliverable: 
        return {
          item_id:     this.item_id,
          room_id:     this.room_id,
          number:      this.item.number,
          to:          rootStore.userStore.getItemByID((this.item as Question)["to"])?.getFullName || null,
          title:       this.item.title,
          created_at:  format(this.created_at, "yyyy-MM-dd HH:mm"),
          start_date:  (this.item as Deliverable).start_date ? format((this.item as Deliverable).start_date, "yyyy-MM-dd") : "",
          end_date:    this.item.date ? format(this.item.date, "yyyy-MM-dd") : "",
          delay_date:  (this.item as Deliverable).delay_date ? format((this.item as Deliverable).delay_date, "yyyy-MM-dd") : "",
          status:      i18next.t(this.item.getStatus) as string,
          _status:     this.item.getStatus,
          meetingroom: rootStore.spaceStore.getItemByID(this.item.room_id)?.title || null,
        }
      default: 
        return {
          avatar:      rootStore.userStore.getItemByID(this.user_id)?.getAvatar,
          log:         this,
          number:      this.id,
          user:        rootStore.userStore.getItemByID(this.user_id)?.getFullName || null ,
          created_at:  format(this.created_at, "yyyy-MM-dd HH:mm:ss") ,
          meetingroom: rootStore.spaceStore.getItemByID(this.room_id)?.title || null,
        }
    }
  }
}
